import { createSlice, DeepPartial, PayloadAction } from '@reduxjs/toolkit';

import { EffectSubscriptionAdd } from '@/shared/api/protocol-ts/api/billing_new/dto_order_new_pb';
import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { Report_CBCT_IOS_Superimposition_Orientation } from '@/shared/api/protocol-ts/model/dto_report_type_cbct_ios_superimposition_pb';
import { OrderReportType, SliceName } from '@/shared/config';
import { ButtonProps } from '@/shared/ui';

import { OrganizationMember } from '@/entities/organization/config/types';

import { ModalID, type StudyCompareImage } from '../config';

type ConfirmModalData = {
  title: string;
  description?: string;
  okText?: string;
  cancelText?: string;
  okButtonProps?: ButtonProps;
  cancelButtonVariant?: 'primary' | 'secondary' | 'tertiary';
  bodyClassName?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
};

type MIRenderModalData = {
  toothID: string;
  assetID: string;
  isSelectionEnabled?: boolean;
};

type TeethNumberingModalData = {
  image: {
    src: string;
    width?: number;
    height?: number;
    path?: string;
    viewOptions?: {
      sharpness?: number;
      invert: boolean;
      wwwc: { ww: number; wc: number };
    };
    zoom?: number;
  };
};

type AddConditionModalData = {
  toothID: string;
  displayToothNumber: string;
  reportID: string;
  ISONumber: number | undefined;
  conditionID?: string;
};

type MPRFullScreenModalData = {
  nrrd: string;
};

type UploadStudyModalData = {
  patientID: string;
};

type OrderReportModalData = {
  reportType: OrderReportType;
};

type SharePatientModalData = {
  patientID: string;
};

type ProtectedSharingModalData = {
  accessCode: string;
  invitationCreateDate: number | Date;
};

type RevokeSharedPatientModalData = {
  patientID: string;
  sharedPatientDoctorID: string;
  invitationID: string;
  isInviteAccepted: boolean;
};

type AddStaffModalData = {
  actionType: 'Add' | 'Edit';
  editingUserID: string;
  doctorInfo?: OrganizationMember;
};

type OrderModelData = {
  title?: string;
  type?: 'InputStudioImplant' | 'InputStudioOrtho';
};

type CongratsModalData = {
  redirectTo?: string;
  title?: string | JSX.Element;
  message?: string | JSX.Element;
  buttonText?: string | JSX.Element;
  isTertiaryButton?: boolean;
  expiredTime?: number;
};

type ResetPasswordErrorModalData = {
  errorType: 'ExpiredTime';
};

type PatientModalData = {
  patientID: string;
};

type BillingInfoModalData = {
  isEdit: boolean;
  invoiceID?: string;
  effectToAdd?: DeepPartial<{
    SubscriptionAdd?: EffectSubscriptionAdd;
  }>;
};

type ReportInfoModalData = {
  sourceStudyIDs: string[];
  reportType: ReportType;
  orientation?: Report_CBCT_IOS_Superimposition_Orientation;
  shouldHideDownload?: boolean;
  /** When set, the destructive footer action reads "Hide" instead of "Delete". */
  hideMode?: boolean;
  /** A hidden report only offers Restore and permanent Delete, each when its handler is provided. */
  isHidden?: boolean;
  removeReport?: () => Promise<void>;
  restoreReport?: () => Promise<void>;
  downloadReport?: () => Promise<void>;
};

type EditAlbumModalData = {
  albumID: string;
  albumName: string;
  time: Date | undefined;
};

type StudyCompareModalData = {
  images: StudyCompareImage[];
  mainID: string;
  secondaryID: string;
};

type DentalPhotoSliderData = {
  albumID: string;
  activePhotoID: string;
  canEditAlbums: boolean;
};

type TreatingDoctorsData = {
  patientID?: string;
  currentDoctorIDs?: string[];
};

type AllSharedWithModalData = {
  patientID: string;
};

type ClinicalCaseDescriptionEditorModalData = {
  patientID: string;
};

type TranscriptorRecordingModalData = {
  patientID: string;
  encounterID?: string;
};

type TranscriptorContinueRecordingModalData = {
  patientID: string;
  encounterID: string;
  schemaId?: string;
  language?: string;
};

type DentalPhotosUploadModalData = {
  albumID?: string;
  scrollToWidgetOnUpload?: boolean;
};

type RawMeshViewerInfoModalData = {
  studyID: string;
  canDelete: boolean;
  onRemove: () => void;
};

type MobileDentalPhotoUploadMenuModalData = {
  patientID: string;
  albumID?: string;
};

// TODO: [m/8] data validation should take place when opening a modal window.
//  At the moment you can open a modal window without passing all the necessary
// fields to it

type OpenModalPayload = {
  modalID: ModalID;
  data?:
    | ConfirmModalData
    | AddConditionModalData
    | MIRenderModalData
    | MPRFullScreenModalData
    | TeethNumberingModalData
    | UploadStudyModalData
    | ProtectedSharingModalData
    | RevokeSharedPatientModalData
    | OrderModelData
    | AddStaffModalData
    | CongratsModalData
    | ResetPasswordErrorModalData
    | PatientModalData
    | BillingInfoModalData
    | OrderReportModalData
    | ReportInfoModalData
    | EditAlbumModalData
    | DentalPhotoSliderData
    | StudyCompareModalData
    | TreatingDoctorsData
    | AllSharedWithModalData
    | ClinicalCaseDescriptionEditorModalData
    | TranscriptorRecordingModalData
    | TranscriptorContinueRecordingModalData
    | DentalPhotosUploadModalData
    | RawMeshViewerInfoModalData
    | MobileDentalPhotoUploadMenuModalData;
};

export type ModalState = {
  [ModalID.AddCondition]: {
    visible: boolean;
    data: AddConditionModalData;
  };
  [ModalID.Confirm]: {
    visible: boolean;
    data: ConfirmModalData;
    loading?: boolean;
  };
  [ModalID.MIRenderModal]: {
    visible: boolean;
    data: MIRenderModalData;
  };
  [ModalID.TeethNumberingModal]: {
    visible: boolean;
    data: TeethNumberingModalData;
  };
  [ModalID.MPRFullScreenModal]: {
    visible: boolean;
    data: {
      nrrd: string;
      toothID: string;
    };
  };
  [ModalID.UploadStudy_DEPRECATED]: {
    visible: boolean;
    data: UploadStudyModalData;
  };
  [ModalID.OrderReport]: {
    visible: boolean;
    data: OrderReportModalData;
  };
  [ModalID.SharePatient]: {
    visible: boolean;
    data: SharePatientModalData;
  };
  [ModalID.ProtectedSharing]: {
    visible: boolean;
    data: ProtectedSharingModalData;
  };
  [ModalID.EditInfoModal]: {
    visible: boolean;
    data: unknown;
  };
  [ModalID.PermissionsModal]: {
    visible: boolean;
  };
  [ModalID.AddStaffModal]: {
    visible: boolean;
    data: AddStaffModalData;
  };
  [ModalID.EditCompanyInfoModal]: {
    visible: boolean;
  };
  [ModalID.GetFullAccessModal]: {
    visible: boolean;
  };
  [ModalID.CongratsModal]: {
    visible: boolean;
    data: CongratsModalData;
  };
  [ModalID.NewAccountModal]: {
    visible: boolean;
  };
  [ModalID.RevokeSharedPatientModal]: {
    visible: boolean;
    data: RevokeSharedPatientModalData;
  };
  [ModalID.OrderOrthoORImplantModal]: {
    visible: boolean;
    data: OrderModelData;
  };
  [ModalID.Order3DSegmentronModal]: {
    visible: boolean;
  };
  [ModalID.BillingInformationModal]: {
    visible: boolean;
    data: BillingInfoModalData;
  };
  [ModalID.CancelSubscriptionModal]: {
    visible: boolean;
  };
  [ModalID.ResetPasswordErrorModal]: {
    visible: boolean;
    data: ResetPasswordErrorModalData;
  };
  [ModalID.PatientRemovalConfirmationModal]: {
    visible: boolean;
    data: PatientModalData;
  };
  [ModalID.PatientModalForm]: {
    visible: boolean;
    data: PatientModalData;
  };
  [ModalID.DiagnocoinHistoryModal]: {
    visible: boolean;
  };
  [ModalID.PauseOrActivateSubscriptionModal]: {
    visible: boolean;
  };
  [ModalID.AboutLabelingModal]: {
    visible: boolean;
  };
  [ModalID.ReportInfo]: {
    visible: boolean;
    data: ReportInfoModalData;
  };
  [ModalID.DentalPhotosUpload]: {
    visible: boolean;
    data: DentalPhotosUploadModalData;
  };
  [ModalID.EditAlbumModal]: {
    visible: boolean;
    data: EditAlbumModalData;
  };
  [ModalID.DentalPhotoSlider]: {
    visible: boolean;
    data: DentalPhotoSliderData;
  };
  [ModalID.StudyCompare]: {
    visible: boolean;
    data: StudyCompareModalData;
  };
  [ModalID.MobileOrderReportModal]: {
    visible: boolean;
  };
  [ModalID.TreatingDoctorsModal]: {
    visible: boolean;
    data: TreatingDoctorsData;
  };
  [ModalID.TreatingDoctorsFilterDrawer]: {
    visible: boolean;
    data: TreatingDoctorsData;
  };
  [ModalID.TreatingDoctorsSelectDrawer]: {
    visible: boolean;
    data: TreatingDoctorsData;
  };
  [ModalID.HeaderDrawer]: {
    visible: boolean;
  };
  [ModalID.AllSharedWith]: {
    visible: boolean;
    data: AllSharedWithModalData;
  };
  [ModalID.ClinicalCaseDescriptionEditorModal]: {
    visible: boolean;
    data: ClinicalCaseDescriptionEditorModalData;
  };
  [ModalID.TranscriptorRecording]: {
    visible: boolean;
    data: TranscriptorRecordingModalData;
  };
  [ModalID.TranscriptorContinueRecording]: {
    visible: boolean;
    data: TranscriptorContinueRecordingModalData;
  };
  [ModalID.FDADentalProfessionalConfirmationModal]: {
    visible: boolean;
  };
  [ModalID.RawMeshViewerInfo]: {
    visible: boolean;
    data: RawMeshViewerInfoModalData;
  };
  [ModalID.MobileDentalPhotoUploadMenu]: {
    visible: boolean;
    data: MobileDentalPhotoUploadMenuModalData;
  };
};

const initialState: ModalState = {
  [ModalID.AddCondition]: {
    visible: false,
    data: {
      toothID: '',
      displayToothNumber: '',
      reportID: '',
      ISONumber: undefined,
      conditionID: '',
    },
  },
  [ModalID.Confirm]: {
    visible: false,
    loading: false,
    data: {
      title: '',
      description: '',
      okText: '',
      okButtonProps: {},
      cancelText: '',
      onConfirm: () => {},
      onCancel: () => {},
      bodyClassName: '',
      cancelButtonVariant: 'secondary',
    },
  },
  [ModalID.MIRenderModal]: {
    visible: false,
    data: {
      toothID: '',
      assetID: '',
    },
  },
  [ModalID.TeethNumberingModal]: {
    visible: false,
    data: {
      image: {
        src: '',
      },
    },
  },
  [ModalID.MPRFullScreenModal]: {
    visible: false,
    data: {
      nrrd: '',
      toothID: '',
    },
  },
  [ModalID.UploadStudy_DEPRECATED]: {
    visible: false,
    data: {
      patientID: '',
    },
  },
  [ModalID.OrderReport]: {
    visible: false,
    data: {
      reportType: 'CBCT',
    },
  },
  [ModalID.SharePatient]: {
    visible: false,
    data: {
      patientID: '',
    },
  },
  [ModalID.ProtectedSharing]: {
    visible: false,
    data: {
      accessCode: '',
      invitationCreateDate: 0,
    },
  },
  [ModalID.EditInfoModal]: {
    visible: false,
    data: {},
  },
  [ModalID.PermissionsModal]: {
    visible: false,
  },
  [ModalID.AddStaffModal]: {
    visible: false,
    data: {
      actionType: 'Add',
      editingUserID: '',
    },
  },
  [ModalID.EditCompanyInfoModal]: {
    visible: false,
  },
  [ModalID.GetFullAccessModal]: {
    visible: false,
  },
  [ModalID.CongratsModal]: {
    visible: false,
    data: {
      title: '',
      message: '',
      redirectTo: '',
      buttonText: '',
      isTertiaryButton: false,
    },
  },
  [ModalID.NewAccountModal]: {
    visible: true,
  },
  [ModalID.RevokeSharedPatientModal]: {
    visible: false,
    data: {
      patientID: '',
      sharedPatientDoctorID: '',
      invitationID: '',
      isInviteAccepted: false,
    },
  },
  [ModalID.OrderOrthoORImplantModal]: {
    visible: false,
    data: {
      title: '',
      type: 'InputStudioImplant',
    },
  },
  [ModalID.Order3DSegmentronModal]: {
    visible: false,
  },
  [ModalID.BillingInformationModal]: {
    visible: false,
    data: {
      isEdit: true,
      invoiceID: '',
      effectToAdd: {},
    },
  },
  [ModalID.CancelSubscriptionModal]: {
    visible: false,
  },
  [ModalID.ResetPasswordErrorModal]: {
    visible: false,
    data: {
      errorType: 'ExpiredTime',
    },
  },
  [ModalID.PatientRemovalConfirmationModal]: {
    visible: false,
    data: {
      patientID: '',
    },
  },
  [ModalID.PatientModalForm]: {
    visible: false,
    data: {
      patientID: '',
    },
  },
  [ModalID.DiagnocoinHistoryModal]: {
    visible: false,
  },
  [ModalID.PauseOrActivateSubscriptionModal]: {
    visible: false,
  },
  [ModalID.AboutLabelingModal]: {
    visible: false,
  },
  [ModalID.ReportInfo]: {
    visible: false,
    data: {
      sourceStudyIDs: [],
      reportType: ReportType.ReportType_InvalidValue,
      shouldHideDownload: false,
    },
  },
  [ModalID.DentalPhotosUpload]: {
    visible: false,
    data: {
      albumID: '',
      scrollToWidgetOnUpload: false,
    },
  },
  [ModalID.EditAlbumModal]: {
    visible: false,
    data: {
      albumID: '',
      albumName: '',
      time: undefined,
    },
  },
  [ModalID.StudyCompare]: {
    visible: false,
    data: {
      images: [],
      mainID: '',
      secondaryID: '',
    },
  },
  [ModalID.DentalPhotoSlider]: {
    visible: false,
    data: {
      albumID: '',
      activePhotoID: '',
      canEditAlbums: false,
    },
  },
  [ModalID.MobileOrderReportModal]: {
    visible: false,
  },
  [ModalID.TreatingDoctorsModal]: {
    visible: false,
    data: {
      patientID: '',
      currentDoctorIDs: [],
    },
  },
  [ModalID.TreatingDoctorsFilterDrawer]: {
    visible: false,
    data: {
      patientID: '',
      currentDoctorIDs: [],
    },
  },
  [ModalID.TreatingDoctorsSelectDrawer]: {
    visible: false,
    data: {
      patientID: '',
      currentDoctorIDs: [],
    },
  },
  [ModalID.AllSharedWith]: {
    visible: false,
    data: {
      patientID: '',
    },
  },
  [ModalID.HeaderDrawer]: {
    visible: false,
  },
  [ModalID.ClinicalCaseDescriptionEditorModal]: {
    visible: false,
    data: {
      patientID: '',
    },
  },
  [ModalID.TranscriptorRecording]: {
    visible: false,
    data: {
      patientID: '',
    },
  },
  [ModalID.TranscriptorContinueRecording]: {
    visible: false,
    data: {
      patientID: '',
      encounterID: '',
    },
  },
  [ModalID.FDADentalProfessionalConfirmationModal]: {
    visible: false,
  },
  [ModalID.RawMeshViewerInfo]: {
    visible: false,
    data: {
      studyID: '',
      canDelete: false,
      onRemove: () => {},
    },
  },
  [ModalID.MobileDentalPhotoUploadMenu]: {
    visible: false,
    data: {
      patientID: '',
      albumID: undefined,
    },
  },
};

const modalSlice = createSlice({
  name: SliceName.modal,
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<OpenModalPayload>) => {
      const { modalID } = action.payload;
      // A store created before this modal id existed (e.g. a dev HMR-preserved
      // store predating a newly added modal) has no slot for it — initialize
      // from initialState so opening it can't crash on `undefined`.
      if (!state[modalID]) {
        // @ts-expect-error WARN: indexed union assignment, same as closeModal.
        state[modalID] = initialState[modalID];
      }
      state[modalID].visible = true;
      // @ts-expect-error WARN: some modals has not data field. Need to find the way to fix it.
      state[modalID].data = action.payload.data;
    },
    closeModal: (state, { payload }: PayloadAction<ModalID>) => {
      // @ts-expect-error WARN: some modals has not data field. Need to find the way to fix it.
      state[payload] = initialState[payload];
    },
    setConfirmModalLoading: (state, action: PayloadAction<boolean>) => {
      state[ModalID.Confirm].loading = action.payload;
    },
  },
});

export const { actions } = modalSlice;

export default modalSlice.reducer;
